import type { FC } from 'react';
import { useRef, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import Slider from '@wix/da-ds/pkg/Slider';
import PostCompactCard from '@wix/da-shared-react/pkg/PostCompactCard';

import s from './DywPostsSlider.scss';

export interface Props {
  posts: PapiDeviation[];
  className?: string;
  viewAllHref?: string;
  minItemsToShowViewAll?: number;
}

const DywPostsSlider: FC<Props> = ({
  posts,
  className,
  viewAllHref,
  minItemsToShowViewAll = 9,
}) => {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLDivElement>(null);

  const getPostUrl = useCallback(
    (post: PapiDeviation) => {
      // When linking to a specific post with deviationid, don't include username
      // because the post may not belong to that user (it's from the DYW feed).
      // This ensures the post is always pinned to the top on the "Everyone" view.
      return Url.browseDeviantsYouWatchLink('posts', undefined, post.deviationId);
    },
    []
  );

  if (!posts.length) {
    return null;
  }

  const sliderItems = posts.map(post => (
    <div key={post.deviationId} className={s['post-card']}>
      <a href={getPostUrl(post)} title={post.title} className={s['post-link']}>
        <PostCompactCard
          deviation={post}
          className={s['post-compact-card']}
          showUsername
        />
      </a>
    </div>
  ));

  if (viewAllHref && sliderItems.length >= minItemsToShowViewAll) {
    sliderItems.push(
      <a key="view-all" className={s['view-all-slide']} href={viewAllHref}>
        {t('common.viewAll')}
      </a>
    );
  }

  return (
    <section className={classnames(s['root'], className)}>
      <div className={s['slider-wrapper']} ref={sliderRef}>
        <Slider className={s['slider']} contentClassName={s['slider-content']}>
          {sliderItems}
        </Slider>
      </div>
    </section>
  );
};

DywPostsSlider.displayName = 'DywPostsSlider';

export default DywPostsSlider;
